import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Download, Loader2, Tag, Upload, Trash2 } from "lucide-react";
import { fmtDate } from "@/lib/datetime";
import { openFile } from "@/lib/downloads";
import PreviewLightbox from "./PreviewLightbox";

export const LIBRARY_CATEGORIES = [
  { key: "inspiration", label: "Inspiration" },
  { key: "client_file", label: "Fichier client" },
  { key: "resource", label: "Ressource" },
  { key: "charbonier_creation", label: "Création Charbonier Studio" },
  { key: "technical_doc", label: "Document technique" },
  { key: "deliverable", label: "Livrable" },
];

const LEGACY_CATEGORY_LABELS = {
  client_upload: "Fichier client",
  message: "Fichier client",
  final: "Livrable",
};

function labelForCategory(cat) {
  const known = LIBRARY_CATEGORIES.find((c) => c.key === cat);
  if (known) return known.label;
  return LEGACY_CATEGORY_LABELS[cat] || cat || "Autre";
}

function isImage(mime) {
  return (mime || "").toLowerCase().startsWith("image/");
}
function isPdf(mime) {
  return (mime || "").toLowerCase().includes("pdf");
}

function CategoryEditor({ file, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState(file.category);

  const save = async (newVal) => {
    setBusy(true);
    setVal(newVal);
    try {
      await api.patch(`/admin/files/${file.id}/category`, { category: newVal });
      toast.success("Catégorie mise à jour");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
      setVal(file.category);
    } finally {
      setBusy(false);
    }
  };

  return (
    <select
      value={val}
      onChange={(e) => save(e.target.value)}
      disabled={busy}
      className="text-[11px] font-mono uppercase tracking-[0.10em] px-1.5 py-0.5 border border-black/20 bg-white"
      onClick={(e) => e.stopPropagation()}
      data-testid={`library-category-select-${file.id}`}
    >
      {LIBRARY_CATEGORIES.map((c) => (
        <option key={c.key} value={c.key}>
          {c.label}
        </option>
      ))}
      {!LIBRARY_CATEGORIES.some((c) => c.key === val) && (
        <option value={val}>{labelForCategory(val)}</option>
      )}
    </select>
  );
}

function FileTile({ file, onOpen, isAdmin, currentUserId, onCategoryChanged, onDeleted }) {
  const img = isImage(file.mime);
  const pdf = isPdf(file.mime);
  const canPreviewInline = img;
  const canDelete = isAdmin || file.author_id === currentUserId;
  const onClick = () => {
    if (canPreviewInline) onOpen();
    else openFile(file.id);
  };
  const doDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Retirer « ${file.name} » de la bibliothèque ?`)) return;
    try {
      await api.delete(`/orders/${file._orderId}/library/${file.id}`);
      toast.success("Fichier retiré");
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div
      className="border border-black/10 bg-white overflow-hidden group hover:border-[#0E7C90] transition-colors"
      data-testid={`library-tile-${file.id}`}
    >
      <button
        type="button"
        onClick={onClick}
        onContextMenu={(e) => canPreviewInline && e.preventDefault()}
        className="block w-full aspect-[4/3] bg-neutral-100 relative"
      >
        {img ? (
          <img
            src={file.preview_url}
            alt={file.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : pdf && file.thumbnail_url ? (
          <img
            src={file.thumbnail_url}
            alt={`Aperçu de ${file.name}`}
            className="w-full h-full object-contain bg-white"
            loading="lazy"
          />
        ) : pdf ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500">
            <FileText size={40} />
            <span className="mt-2 text-[11px] font-mono uppercase tracking-[0.14em]">PDF</span>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500">
            <FileText size={40} />
            <span className="mt-2 text-[11px] font-mono uppercase tracking-[0.14em]">
              {(file.mime || "fichier").split("/").pop() || "fichier"}
            </span>
          </div>
        )}
      </button>
      <div className="p-3 space-y-1.5">
        <div className="text-sm font-medium truncate" title={file.name}>
          {file.name}
        </div>
        <div className="flex items-center justify-between gap-2 text-[11px] font-mono text-neutral-500">
          <span className="truncate">
            {file.author_name || "—"} · {fmtDate(file.created_at)}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openFile(file.id);
              }}
              className="hover:text-[#0E7C90]"
              title="Télécharger"
              data-testid={`library-download-${file.id}`}
            >
              <Download size={12} />
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={doDelete}
                className="hover:text-red-600"
                title="Retirer"
                data-testid={`library-delete-${file.id}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px]">
          {isAdmin ? (
            <CategoryEditor file={file} onChanged={onCategoryChanged} />
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-100 text-neutral-600 font-mono uppercase tracking-[0.10em]">
              <Tag size={10} />
              {labelForCategory(file.category)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function LibraryUploader({ orderId, isAdmin, onUploaded }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(isAdmin ? "client_file" : "client_file");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) {
      toast.error("Sélectionnez un fichier à ajouter.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    // Côté client : catégorie forcée à "client_file" (Fichier client).
    // Côté admin : la valeur choisie dans le sélecteur.
    fd.append("category", isAdmin ? category : "client_file");
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/library/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fichier ajouté à la bibliothèque");
      setFile(null);
      setCategory("client_file");
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-dashed border-[#0E7C90]/40 bg-[#0E7C90]/[.04] p-4 md:p-5"
      data-testid="library-uploader"
    >
      <div className="cs-eyebrow text-[#0E7C90] inline-flex items-center gap-1">
        <Upload size={12} /> Ajouter un fichier à la bibliothèque
      </div>
      <p className="mt-1 text-[12px] text-neutral-600">
        {isAdmin
          ? "Partagez une inspiration, un document ou un fichier de travail. Choisissez sa catégorie avant de l'envoyer."
          : "Partagez une inspiration, un document ou un fichier de travail. Il sera ajouté à vos fichiers client."}
      </p>
      <div
        className={`mt-3 grid gap-3 ${
          isAdmin
            ? "md:grid-cols-[1fr_auto_auto] md:items-end"
            : "md:grid-cols-[1fr_auto] md:items-end"
        }`}
      >
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
          data-testid="library-upload-file"
        />
        {isAdmin && (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="cs-input md:min-w-[220px]"
            data-testid="library-upload-category"
          >
            {LIBRARY_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={busy || !file}
          className="cs-btn-primary inline-flex items-center gap-2 justify-center"
          data-testid="library-upload-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Ajouter
        </button>
      </div>
    </div>
  );
}

export default function FilesLibrary({ orderId, isAdmin, currentUserId, refreshKey }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/library`);
      setFiles(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of files) {
      const key = LIBRARY_CATEGORIES.some((c) => c.key === f.category)
        ? f.category
        : LEGACY_CATEGORY_LABELS[f.category]
        ? Object.keys(LEGACY_CATEGORY_LABELS).includes(f.category)
          ? "client_file"
          : f.category
        : "resource";
      const target = LIBRARY_CATEGORIES.some((c) => c.key === key) ? key : "resource";
      if (!map.has(target)) map.set(target, []);
      map.get(target).push(f);
    }
    return map;
  }, [files]);

  // Images list flat pour la lightbox
  const imageFiles = useMemo(() => files.filter((f) => isImage(f.mime)), [files]);
  const openImageAt = (fileId) => {
    const idx = imageFiles.findIndex((f) => f.id === fileId);
    if (idx >= 0) {
      setLightboxIdx(idx);
      setOpen(true);
    }
  };

  const lightboxPages = imageFiles.map((f) => ({ file_id: f.id, url: f.preview_url }));

  return (
    <div className="cs-card p-6" data-testid="files-library">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="cs-eyebrow text-[#0E7C90]">Fichiers & inspirations</div>
        <div className="text-[12px] font-mono text-neutral-500">
          Bibliothèque du projet · {files.length} fichier{files.length > 1 ? "s" : ""}
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm text-neutral-500 font-mono flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Chargement de la bibliothèque…
        </div>
      )}

      {!loading && files.length === 0 && (
        <div className="mt-4 p-6 border border-dashed border-black/15 bg-neutral-50 text-center">
          <ImageIcon size={22} className="mx-auto text-neutral-400" />
          <p className="mt-2 text-sm text-neutral-600">
            Aucun fichier dans la bibliothèque pour l'instant.
          </p>
          <p className="mt-1 text-[12px] text-neutral-500">
            Les fichiers échangés dans la messagerie et lors du brief apparaîtront
            automatiquement ici.
          </p>
        </div>
      )}

      {!loading && files.length > 0 && (
        <div className="mt-5 space-y-8">
          {LIBRARY_CATEGORIES.map((cat) => {
            const list = grouped.get(cat.key);
            if (!list || list.length === 0) return null;
            return (
              <div key={cat.key} data-testid={`library-group-${cat.key}`}>
                <div className="cs-eyebrow text-neutral-500 mb-3">{cat.label}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {list.map((f) => (
                    <FileTile
                      key={f.id}
                      file={{ ...f, _orderId: orderId }}
                      isAdmin={isAdmin}
                      currentUserId={currentUserId}
                      onOpen={() => openImageAt(f.id)}
                      onCategoryChanged={load}
                      onDeleted={load}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 border-t border-black/10 pt-5">
        <LibraryUploader orderId={orderId} isAdmin={isAdmin} onUploaded={load} />
      </div>

      <PreviewLightbox
        open={open}
        pages={lightboxPages}
        initialIndex={lightboxIdx}
        onClose={() => setOpen(false)}
        title="Bibliothèque du projet"
      />
    </div>
  );
}
